import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TechnicalReportsPanel.css';
import TechnicalReportModal from './TechnicalReportModal';
import TechnicalReportDetail from './TechnicalReportDetail';
import TechniciansModal from './TechniciansModal';

const TechnicalReportsPanel = ({ onOpenChat }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    priority: '',
    search: ''
  });
  const [stats, setStats] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showTechniciansModal, setShowTechniciansModal] = useState(false);
  const [showClientes, setShowClientes] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(false);

  useEffect(() => {
    loadReports();
    loadStats();
  }, [filters, currentPage]);

  const loadReports = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = {
        page: currentPage,
        limit: 20,
        ...filters
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await axios.get('/api/technical-reports', {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      if (response.data.success) {
        setReports(response.data.data);
        setTotalPages(response.data.pagination?.pages || 1);
      }
    } catch (error) {
      console.error('Error cargando reportes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/technical-reports/stats/summary', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
  };

  const loadClientes = async () => {
    try {
      setLoadingClientes(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/clientes', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setClientes(response.data.data || []);
      }
    } catch (error) {
      console.error('Error cargando clientes:', error);
      alert('Error al cargar clientes: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoadingClientes(false);
    }
  };

  const handleToggleView = () => {
    const newShowClientes = !showClientes;
    setShowClientes(newShowClientes);
    if (newShowClientes && clientes.length === 0) {
      loadClientes();
    }
  };

  const handleCreateReport = () => {
    setEditingReport(null);
    setShowModal(true);
  };

  const handleEditReport = (report) => {
    setEditingReport(report);
    setShowModal(true);
  };

  const handleSaveReport = async (reportData) => {
    try {
      const token = localStorage.getItem('token');
      
      if (editingReport) {
        await axios.put(`/api/technical-reports/${editingReport._id}`, reportData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post('/api/technical-reports', reportData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setShowModal(false);
      setEditingReport(null);
      loadReports();
      loadStats();
    } catch (error) {
      console.error('Error guardando reporte:', error);
      alert('Error al guardar el reporte: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('¿Estás seguro de eliminar este reporte técnico?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/technical-reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      loadReports();
      loadStats();
      if (selectedReport?._id === reportId) {
        setSelectedReport(null);
      }
    } catch (error) {
      console.error('Error eliminando reporte:', error);
      alert('Error al eliminar el reporte: ' + (error.response?.data?.error || error.message));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pendiente': return '#ffc107';
      case 'en_proceso': return '#17a2b8';
      case 'completado': return '#28a745';
      case 'cancelado': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'instalacion': return '🔧';
      case 'averia': return '⚠️';
      case 'mantenimiento': return '🔨';
      default: return '📋';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgente': return '#dc3545';
      case 'alta': return '#fd7e14';
      case 'media': return '#ffc107';
      case 'baja': return '#28a745';
      default: return '#6c757d';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="technical-reports-panel">
      <div className="technical-reports-header">
        <h2>{showClientes ? '👥 Clientes Registrados' : '🔧 Reportes Técnicos'}</h2>
        <div className="header-actions">
          <button className="btn-toggle-view" onClick={handleToggleView}>
            {showClientes ? '📋 Ver Reportes' : '👥 Ver Clientes'}
          </button>
          <button className="btn-manage-technicians" onClick={() => setShowTechniciansModal(true)}>
            👷 Gestionar Técnicos
          </button>
          {!showClientes && (
            <button className="btn-create-report" onClick={handleCreateReport}>
              ➕ Nuevo Reporte
            </button>
          )}
        </div>
      </div>

      {/* Vista de Clientes */}
      {showClientes && (
        <div className="clientes-container">
          {loadingClientes ? (
            <div className="loading">Cargando clientes...</div>
          ) : clientes.length === 0 ? (
            <div className="no-data">No hay clientes registrados</div>
          ) : (
            <div className="clientes-grid">
              {clientes.map(cliente => (
                <div key={cliente._id} className="cliente-card">
                  <div className="cliente-header">
                    <h3>{cliente.nombreCompleto}</h3>
                    <span className="cliente-cedula">Cédula: {cliente.cedula}</span>
                  </div>
                  {cliente.imagenCedula && (
                    <div className="cliente-imagen">
                      <img src={cliente.imagenCedula} alt="Cédula" />
                    </div>
                  )}
                  <div className="cliente-info">
                    <div className="info-row">
                      <span className="label">📍 Dirección:</span>
                      <span>{cliente.direccionPrincipal}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">📞 WhatsApp:</span>
                      <span>{cliente.telefonoWhatsApp}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">🌐 WiFi:</span>
                      <span>{cliente.nombreSSIDWifi}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">🔑 Contraseña:</span>
                      <span>{cliente.contrasenaWifi}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">📦 Caja NAT:</span>
                      <span>{cliente.numeroCajaNAT}</span>
                    </div>
                    {cliente.coordenadasGPS && (
                      <div className="info-row">
                        <span className="label">🗺️ GPS:</span>
                        <a 
                          href={`https://www.google.com/maps?q=${cliente.coordenadasGPS.latitud},${cliente.coordenadasGPS.longitud}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="gps-link"
                        >
                          Ver en mapa
                        </a>
                      </div>
                    )}
                    {cliente.tecnicoRegistro && (
                      <div className="info-row">
                        <span className="label">👷 Técnico:</span>
                        <span>{cliente.tecnicoRegistro.name || 'N/A'}</span>
                      </div>
                    )}
                    <div className="info-row">
                      <span className="label">📅 Registro:</span>
                      <span>{new Date(cliente.fechaRegistro).toLocaleDateString('es-ES')}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Estadísticas */}
      {!showClientes && stats && (
        <div className="technical-reports-stats">
          <div className="stat-card">
            <div className="stat-value">{stats.total || 0}</div>
            <div className="stat-label">Total</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#ffc107' }}>{stats.pendientes || 0}</div>
            <div className="stat-label">Pendientes</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#17a2b8' }}>{stats.enProceso || 0}</div>
            <div className="stat-label">En Proceso</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#28a745' }}>{stats.completados || 0}</div>
            <div className="stat-label">Completados</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.instalaciones || 0}</div>
            <div className="stat-label">Instalaciones</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.averias || 0}</div>
            <div className="stat-label">Averías</div>
          </div>
        </div>
      )}

      {/* Filtros */}
      {!showClientes && (
        <div className="technical-reports-filters">
        <input
          type="text"
          placeholder="🔍 Buscar por ticket, cliente, teléfono..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          className="filter-input"
        />
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="filter-select"
        >
          <option value="">Todos los estados</option>
          <option value="pendiente">Pendiente</option>
          <option value="en_proceso">En Proceso</option>
          <option value="completado">Completado</option>
          <option value="cancelado">Cancelado</option>
        </select>
        <select
          value={filters.type}
          onChange={(e) => setFilters({ ...filters, type: e.target.value })}
          className="filter-select"
        >
          <option value="">Todos los tipos</option>
          <option value="instalacion">Instalación</option>
          <option value="averia">Avería</option>
          <option value="mantenimiento">Mantenimiento</option>
          <option value="otro">Otro</option>
        </select>
        <select
          value={filters.priority}
          onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
          className="filter-select"
        >
          <option value="">Todas las prioridades</option>
          <option value="urgente">Urgente</option>
          <option value="alta">Alta</option>
          <option value="media">Media</option>
          <option value="baja">Baja</option>
        </select>
        <button
          onClick={() => setFilters({ status: '', type: '', priority: '', search: '' })}
          className="btn-clear-filters"
        >
          Limpiar
        </button>
      </div>
      )}

      {/* Lista de reportes */}
      {!showClientes && (
      <div className="technical-reports-content">
        <div className="technical-reports-list">
          {loading ? (
            <div className="loading-message">Cargando reportes...</div>
          ) : reports.length === 0 ? (
            <div className="empty-message">No hay reportes técnicos</div>
          ) : (
            reports.map((report) => (
              <div
                key={report._id}
                className={`technical-report-item ${selectedReport?._id === report._id ? 'selected' : ''}`}
                onClick={() => setSelectedReport(report)}
              >
                <div className="report-item-header">
                  <div className="report-ticket-number">{report.ticketNumber}</div>
                  <div
                    className="report-status-badge"
                    style={{ backgroundColor: getStatusColor(report.status) }}
                  >
                    {report.status === 'pendiente' ? 'Pendiente' :
                     report.status === 'en_proceso' ? 'En Proceso' :
                     report.status === 'completado' ? 'Completado' :
                     'Cancelado'}
                  </div>
                </div>
                <div className="report-item-body">
                  <div className="report-type">
                    {getTypeIcon(report.type)} {report.type === 'instalacion' ? 'Instalación' :
                                                  report.type === 'averia' ? 'Avería' :
                                                  report.type === 'mantenimiento' ? 'Mantenimiento' :
                                                  'Otro'}
                  </div>
                  <div className="report-title">{report.title}</div>
                  <div className="report-client">{report.clientName}</div>
                  <div className="report-meta">
                    <span className="report-priority" style={{ color: getPriorityColor(report.priority) }}>
                      {report.priority === 'urgente' ? '🔴 Urgente' :
                       report.priority === 'alta' ? '🟠 Alta' :
                       report.priority === 'media' ? '🟡 Media' :
                       '🟢 Baja'}
                    </span>
                    <span className="report-date">{formatDate(report.createdAt)}</span>
                  </div>
                </div>
                <div className="report-item-actions">
                  <button
                    className="btn-edit-report"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditReport(report);
                    }}
                  >
                    ✏️ Editar
                  </button>
                  <button
                    className="btn-delete-report"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteReport(report._id);
                    }}
                  >
                    🗑️ Eliminar
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Detalle del reporte seleccionado */}
        {selectedReport && (
          <TechnicalReportDetail
            report={selectedReport}
            onUpdate={loadReports}
            onClose={() => setSelectedReport(null)}
            onOpenChat={onOpenChat}
          />
        )}
      </div>
      )}

      {/* Paginación */}
      {!showClientes && totalPages > 1 && (
        <div className="technical-reports-pagination">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Anterior
          </button>
          <span>Página {currentPage} de {totalPages}</span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Siguiente
          </button>
        </div>
      )}

      {/* Modal para crear/editar reporte */}
      {showModal && (
        <TechnicalReportModal
          report={editingReport}
          onSave={handleSaveReport}
          onClose={() => {
            setShowModal(false);
            setEditingReport(null);
          }}
        />
      )}

      {/* Modal para gestionar técnicos */}
      <TechniciansModal
        isOpen={showTechniciansModal}
        onClose={() => setShowTechniciansModal(false)}
      />
    </div>
  );
};

export default TechnicalReportsPanel;
